import { useRef, useState } from 'react';
import type { ModuleMeta } from '../../harness/ModuleRegistry';
import { Sigil } from '../_nav/Sigil';
import { Wordmark } from '../_nav/Wordmark';
import { AtmosphericLayer } from '../_shared/AtmosphericLayer';

// 06 ПРАЙС — texts byte-equal to README.md §5 (heading, body, tier prices, tier bullets).
// Prices "220 000 ₽" / "350 000 ₽" are legal-sensitive per master prompt §0.2
// ("Цены и юридические тексты" — Igor approve gate); do not paraphrase or restructure
// here without Igor's explicit ok. Digits are kept untouched; only the ₽ glyph carries
// the `.module-06-card__rouble` shimmer wrapper (still byte-equal as a unit).
//
// Layout follows the §5 ASCII: wordmark top-left, Lora 40px title (two visual lines
// "Сколько" + "это стоит?"), Helvetica 15px body with `.vibe` on «вайб/лоу/ноу»
// (DESIGN.md §3.4), then two `bg-surface-elevated` cards (radius 16px, padding 32px
// per Wave 3 brief §4.8 uplift) each with a 2px cyan rail + faint cyan glow on the
// left (pattern from DESIGN.md §9.5). Tier label mono row sits above the price.
// On tap the card briefly reveals a «свяжись с нами» microcopy (200ms ghost) — a
// state-driven micro-interaction, not a permanent expand. Calm atmosphere per
// DESIGN.md §7.8 (single sphere, no shafts) keeps focus on the numbers.

export const meta: ModuleMeta = {
  id: '06-price',
  title: 'ПРАЙС · Сколько это стоит',
  status: 'approved',
  viewport: '390x844',
};

interface PriceTierData {
  /** Mono uppercase label rendered above the price. Not a master-prompt rubric —
   *  visual scaffolding for Wave 3 weight per orchestrator note. */
  tier: string;
  /** Digits portion of the price; kept verbatim per §0.2 approve gate. */
  digits: string;
  /** ₽ glyph rendered with the shimmer wrapper — still byte-equal as a whole. */
  unit: string;
  bullets: React.ReactNode;
}

// Igor round-3 2026-06-11: 3 tiers (was 2). Bullets reworded + Тариф III =
// «Сезонные пакеты», whose value line is «10 механик · −30%». Igor gave NO ₽
// price for tier III, so the discount «−30%» is the focal figure (not an
// invented number). The «Итого» note from his doc is omitted pending his
// clarification (unclear role).
const TIERS: PriceTierData[] = [
  {
    tier: 'Тариф I',
    digits: '220 000',
    unit: '₽',
    bullets: (
      <>
        Контентный и/или
        <br />
        геймификационный продукт
      </>
    ),
  },
  {
    tier: 'Тариф II',
    digits: '350 000',
    unit: '₽',
    bullets: <>Сервисный продукт</>,
  },
  {
    tier: 'Тариф III',
    digits: '−30%',
    unit: '',
    bullets: (
      <>
        Сезонные пакеты ·
        <br />
        10 механик
      </>
    ),
  },
];

export default function PriceModule() {
  // Per-card expanded flag — tap triggers a 200ms «свяжись с нами» reveal then
  // collapses back. State is local; no event bus / Telegram SDK call.
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const expandTimer = useRef<number | null>(null);

  const handleTierTap = (idx: number) => {
    if (expandTimer.current !== null) {
      window.clearTimeout(expandTimer.current);
    }
    setExpandedIdx(idx);
    expandTimer.current = window.setTimeout(() => {
      setExpandedIdx(null);
      expandTimer.current = null;
    }, 200);
  };

  return (
    <section
      className="relative min-h-full w-full bg-surface-main text-text-primary"
      style={{
        paddingTop: 'max(var(--safe-top, 0px), 0.75rem)',
        paddingBottom: 'max(var(--safe-bottom, 0px), 1.5rem)',
      }}
    >
      <AtmosphericLayer spheres={1} shafts={0} intensity="calm" />

      <Wordmark variant="nav" />

      {/* DESIGN.md §10 — 06 carries the standard atmosphere sigil watermark
          (120px, opacity 0.04, fixed lower-right per §8.1). */}
      <div
        className="pointer-events-none absolute bottom-sp-8 right-sp-5 z-[1]"
        aria-hidden
      >
        <Sigil variant="watermark" />
      </div>

      <div
        className="relative z-10 flex min-h-full flex-col px-sp-6"
        style={{ paddingTop: 'calc(max(var(--safe-top, 0px), 0.75rem) + 64px)' }}
      >
        <div className="flex flex-col gap-sp-6 pb-sp-10">
          <p className="text-rubric-mono">// ПРАЙС</p>

          <h1 className="font-display text-display leading-[1.10] tracking-[-0.02em] text-text-primary">
            Сколько
            <br />
            это стоит?
          </h1>

          <p className="max-w-[320px] font-ui text-body leading-[1.5] text-text-primary">
            Технологии <span className="vibe">вайб/лоу/ноу</span> сокращают
            трудозатраты на производство, и мы сознательно делимся этой экономией
            с клиентом.
          </p>

          <span className="module-06-hairline" aria-hidden />

          <div className="mt-sp-2 flex flex-col gap-sp-4">
            {TIERS.map((tier, i) => {
              const isExpanded = expandedIdx === i;
              return (
                <article
                  key={tier.digits}
                  className={`module-06-card module-06-card--${i + 1} relative overflow-hidden rounded-[16px] text-center${
                    isExpanded ? ' is-expanded' : ''
                  }`}
                  onClick={() => handleTierTap(i)}
                  onTouchStart={() => handleTierTap(i)}
                >
                  {/* 2px cyan rail + faint glow per Wave 3 brief §4.8. */}
                  <span className="module-06-card__rail" aria-hidden />
                  <p className="module-06-card__tier">{tier.tier}</p>
                  <p className="font-display text-price leading-[1.0] tracking-[-0.01em] text-text-primary">
                    {tier.digits}{' '}
                    <span className="module-06-card__rouble">{tier.unit}</span>
                  </p>
                  <p className="mt-sp-3 font-ui text-rubric leading-[1.4] text-text-primary">
                    {tier.bullets}
                  </p>
                  <span className="module-06-card__hint" aria-live="polite">
                    свяжись с нами
                  </span>
                </article>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className="hint-arrow-pulse pointer-events-none absolute bottom-sp-7 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center gap-sp-3 text-text-muted"
        aria-hidden
      >
        <span className="inline-block h-[14px] w-[14px] rounded-full border border-current" />
        <span className="text-[1.25rem] leading-none">↓</span>
      </div>
    </section>
  );
}
